import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { FiUpload, FiGlobe, FiClock, FiCalendar, FiLink, FiBell, FiTool } from 'react-icons/fi';
import { HiOutlineCheck, HiOutlineArrowSmRight } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { toastService } from '../../../../services/toast';
import { settingsApi } from '../../../../services/api/settings.api';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const GeneralSettings = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    siteName: '',
    logo: null,
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24',
    maintenanceMode: false,
    maintenanceMessage: '',
    maintenanceDuration: 2,
    maintenanceAutoDisable: true,
    durationEnabled: true,
    maintenanceEndDate: null
  });

  // جلب إعدادات الصيانة
  const fetchSettings = async () => {
    try {
      const response = await settingsApi.getMaintenanceSettings();
      setSettings(prev => ({
        ...prev,
        maintenanceMode: response.is_enabled,
        maintenanceMessage: response.message || '',
        maintenanceDuration: response.duration_hours || 2,
        maintenanceAutoDisable: response.auto_disable,
        durationEnabled: response.duration_enabled,
        maintenanceEndDate: response.ends_at
      }));
    } catch (error) {
      toastService.error('حدث خطأ أثناء جلب الإعدادات');
    }
  };

  useEffect(() => {
    // جلب الإعدادات مرة واحدة عند فتح الصفحة
    fetchSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    if (e.target.files?.[0]) {
      setSettings(prev => ({ ...prev, logo: e.target.files[0] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // حساب وقت انتهاء الصيانة
      const endsAt = settings.maintenanceMode && settings.durationEnabled
        ? new Date(Date.now() + settings.maintenanceDuration * 60 * 60 * 1000).toISOString()
        : null;

      // تحديث إعدادات الصيانة
      await settingsApi.updateMaintenanceSettings({
        is_enabled: settings.maintenanceMode,
        message: settings.maintenanceMessage,
        duration_hours: settings.maintenanceDuration,
        auto_disable: settings.maintenanceAutoDisable,
        duration_enabled: settings.durationEnabled,
        ends_at: endsAt
      });

      toastService.success('تم حفظ الإعدادات بنجاح');
    } catch (error) {
      toastService.error('حدث خطأ أثناء حفظ الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      name: 'إعدادات عامة',
      icon: FiGlobe,
      content: (
        <div className="p-6">
          <form id="generalSettingsForm" onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* معلومات الموقع */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">معلومات الموقع</h3>
                {/* اسم الموقع */}
                <div>
                  <label htmlFor="siteName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    اسم الموقع
                  </label>
                  <input
                    type="text"
                    id="siteName"
                    name="siteName"
                    value={settings.siteName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="أدخل اسم الموقع"
                  />
                </div>

                {/* الشعار */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    شعار الموقع
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      {settings.logo ? (
                        <img
                          src={URL.createObjectURL(settings.logo)}
                          alt="شعار الموقع"
                          className="w-16 h-16 object-contain rounded-lg bg-white dark:bg-gray-800 p-2"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-white dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center">
                          <FiUpload className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="flex items-center justify-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600">
                        <FiUpload className="w-5 h-5 ml-2 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">اختر شعاراً</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="hidden"
                        />
                      </label>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        يفضل استخدام صورة بحجم 512×512 بيكسل بصيغة PNG أو SVG
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* إعدادات التوقيت */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">إعدادات التوقيت</h3>
                <div className="space-y-6">
                  {/* تنسيق التاريخ */}
                  <div>
                    <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <div className="flex items-center gap-2">
                        <FiCalendar className="w-4 h-4" />
                        <span>تنسيق التاريخ</span>
                      </div>
                    </label>
                    <select
                      id="dateFormat"
                      name="dateFormat"
                      value={settings.dateFormat}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY/MM/DD">YYYY/MM/DD</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    </select>
                  </div>

                  {/* تنسيق الوقت */}
                  <div>
                    <label htmlFor="timeFormat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <div className="flex items-center gap-2">
                        <FiClock className="w-4 h-4" />
                        <span>تنسيق الوقت</span>
                      </div>
                    </label>
                    <select
                      id="timeFormat"
                      name="timeFormat"
                      value={settings.timeFormat}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="12">12 ساعة</option>
                      <option value="24">24 ساعة</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )
    },
    {
      name: 'روابط النظام',
      icon: FiLink,
      content: (
        <div className="p-6">
          <form id="siteLinksForm" onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  رابط لوحة التحكم
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="admin/dashboard"
                      defaultValue="admin/dashboard"
                    />
                    <p className="mt-1 text-sm text-gray-500">المسار الحالي</p>
                  </div>
                  <span className="text-gray-500 dark:text-gray-400">
                    <FiLink className="w-5 h-5" />
                  </span>
                  <div className="flex-1">
                    <input
                      type="text"
                      className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="admin"
                    />
                    <p className="mt-1 text-sm text-gray-500">المسار الجديد</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  البادئة الافتراضية
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="/"
                />
                <p className="mt-1 text-sm text-gray-500">
                  البادئة التي سيتم إضافتها لجميع روابط النظام
                </p>
              </div>
            </div>
          </form>
        </div>
      )
    },
    {
      name: 'رسائل النظام',
      icon: FiBell,
      content: (
        <div className="p-6">
          <form id="systemMessagesForm" onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  رسالة التنبيه الافتراضية
                </label>
                <textarea
                  className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="2"
                  placeholder="تنبيه! {message}"
                />
                <p className="mt-1 text-sm text-gray-500">
                  استخدم {'{'}"message"{'}'} كمتغير لإدراج نص الرسالة
                </p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  رسالة التحذير الافتراضية
                </label>
                <textarea
                  className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="2"
                  placeholder="تحذير! {message}"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  رسالة الخطأ الافتراضية
                </label>
                <textarea
                  className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="2"
                  placeholder="خطأ! {message}"
                />
              </div>
            </div>
          </form>
        </div>
      )
    },
    {
      name: 'وضع الصيانة',
      icon: FiTool,
      content: (
        <div className="p-6">
          <div className="space-y-6">
            <div className="p-6 space-y-6">
              {/* تفعيل وضع الصيانة */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">تفعيل وضع الصيانة</span>
                  <span className="mt-1 text-sm text-gray-500">عند التفعيل، سيتم عرض صفحة الصيانة للزوار</span>
                </div>
                <button
                  type="button"
                  className={`switch-rtl ${
                    settings.maintenanceMode ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-400 dark:bg-gray-600'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                  onClick={() => setSettings(prev => ({ ...prev, maintenanceMode: !prev.maintenanceMode }))}
                >
                  <span
                    className={`${
                      settings.maintenanceMode ? '-translate-x-6' : '-translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </button>
              </div>

              {/* رسالة الصيانة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  رسالة الصيانة
                  <span className="text-sm text-gray-500 mr-2">ستظهر هذه الرسالة للزوار أثناء وضع الصيانة</span>
                </label>
                <textarea
                  name="maintenanceMessage"
                  value={settings.maintenanceMessage}
                  onChange={handleInputChange}
                  rows={4}
                  className="input-base resize-none w-full"
                  placeholder="مثال: الموقع تحت الصيانة حالياً، نعتذر عن الإزعاج... سنعود قريباً!"
                />
              </div>

              {/* تفعيل المدة */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">تفعيل مدة الصيانة</span>
                  <span className="mt-1 text-sm text-gray-500">عند التفعيل، سيتم عرض وقت انتهاء الصيانة المتوقع</span>
                </div>
                <button
                  type="button"
                  className={`switch-rtl ${
                    settings.durationEnabled ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-400 dark:bg-gray-600'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                  onClick={() => setSettings(prev => ({ ...prev, durationEnabled: !prev.durationEnabled }))}
                >
                  <span
                    className={`${
                      settings.durationEnabled ? '-translate-x-6' : '-translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </button>
              </div>

              {/* مدة الصيانة */}
              {settings.durationEnabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    مدة الصيانة (بالساعات)
                  </label>
                  <input
                    type="number"
                    name="maintenanceDuration"
                    value={settings.maintenanceDuration}
                    onChange={handleInputChange}
                    min="1"
                    className="input-base"
                  />
                </div>
              )}

              {/* إعادة تشغيل تلقائي */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">إيقاف الصيانة تلقائياً</span>
                  <span className="mt-1 text-sm text-gray-500">إيقاف وضع الصيانة تلقائياً بعد انتهاء المدة</span>
                </div>
                <button
                  type="button"
                  className={`switch-rtl ${
                    settings.maintenanceAutoDisable ? 'bg-green-500 dark:bg-green-600' : 'bg-gray-400 dark:bg-gray-600'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                  onClick={() => setSettings(prev => ({ ...prev, maintenanceAutoDisable: !prev.maintenanceAutoDisable }))}
                >
                  <span
                    className={`${
                      settings.maintenanceAutoDisable ? '-translate-x-6' : '-translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </button>
              </div>

              {/* معاينة صفحة الصيانة */}
              <div className="mt-8">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">معاينة صفحة الصيانة</h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                  <div className="text-center">
                    <FiTool className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      الموقع تحت الصيانة
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {settings.maintenanceMessage || 'نعتذر عن الإزعاج، نحن نقوم ببعض التحسينات. سنعود قريباً!'}
                    </p>
                    {settings.maintenanceMode && settings.durationEnabled && settings.maintenanceDuration > 0 && (
                      <p className="text-sm text-gray-500">
                        وقت انتهاء الصيانة المتوقع: {new Date(settings.maintenanceEndDate || Date.now() + settings.maintenanceDuration * 60 * 60 * 1000).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          second: 'numeric',
                          hour12: true
                        })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div>
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">إعدادات عامة</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate('/admin/dashboard/settings')}
              className="group relative p-1 text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
            >
              <HiOutlineArrowSmRight className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                رجوع للإعدادات
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
            <button
              onClick={handleSubmit}
              className="group relative p-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-500 dark:hover:text-emerald-400"
            >
              <HiOutlineCheck className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                حفظ التغييرات
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
          </div>
        </div>
      </div>
      <div className="p-6">
        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          <Tab.List className="flex gap-2 rounded-xl bg-gray-100 dark:bg-gray-700 p-1 mb-6">
            {tabs.map((tab, idx) => (
              <Tab
                key={idx}
                className={({ selected }) =>
                  classNames(
                    'w-full rounded-lg py-3 text-sm font-medium leading-5',
                    'focus:outline-none',
                    'flex items-center justify-center gap-2',
                    selected
                      ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-white/[0.12] dark:hover:bg-gray-500/50'
                  )
                }
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels>
            {tabs.map((tab, idx) => (
              <Tab.Panel
                key={idx}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
              >
                {tab.content}
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

export default GeneralSettings;
